// Per-instance parameter row. Visual match for bb-traderx wizard's compact inline
// editor: small numeric input + ⓘ tooltip + label, flex-wrapped, all in one row.

import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { IndicatorParamDescriptor } from "../../api/indicators";

interface Props {
  descriptors: IndicatorParamDescriptor[];
  values: Record<string, number>;
  onChange: (key: string, value: number) => void;
}

function clamp(v: number, min: number, max: number): number {
  if (!Number.isFinite(v)) return min;
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

export function IndicatorParamEditor({ descriptors, values, onChange }: Props) {
  if (descriptors.length === 0) {
    return <div className="text-[10px] text-muted-foreground italic">No parameters.</div>;
  }

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1.5 px-1">
      {descriptors.map((d) => {
        const raw = values[d.key];
        const value = Number.isFinite(raw) ? raw : d.default;
        const outOfRange = value < d.min || value > d.max;
        return (
          <div key={d.key} className="flex items-center gap-1.5">
            {d.description && (
              <Tooltip delayDuration={120}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    tabIndex={-1}
                    className="shrink-0 text-muted-foreground/60 hover:text-primary transition-colors"
                  >
                    <Info className="size-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px] text-xs leading-snug">
                  <p>{d.description}</p>
                  <p className="text-muted-foreground mt-1">
                    Range: {d.min}–{d.max} · Default: {d.default}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            <label className="text-[10px] text-muted-foreground whitespace-nowrap">
              {d.label}
            </label>
            <input
              type="number"
              min={d.min}
              max={d.max}
              step={d.step}
              value={value}
              onChange={(e) =>
                onChange(d.key, clamp(parseFloat(e.target.value) || d.default, d.min, d.max))
              }
              className={cn(
                "w-14 h-6 rounded-md border bg-background/60 text-[11px] text-center font-mono outline-none px-1 transition-colors",
                outOfRange
                  ? "border-red-500/60 text-red-400"
                  : "border-primary/40 text-primary focus:border-primary",
              )}
            />
          </div>
        );
      })}
    </div>
  );
}
